import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Building2, Factory, Headset, AlertTriangle, TrendingUp, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { SEVERITY_COLORS } from "../lib/fields";

const AMBER = "#F5A623";

export function GroupRollup({ accountId }) {
  const { t, isManager, plLabel } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [qbrLoading, setQbrLoading] = useState(false);

  const load = useCallback(() => {
    api.get(`/accounts/${accountId}/group-rollup`).then((r) => setData(r.data)).catch(() => {});
  }, [accountId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isManager) api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [isManager]);

  const downloadQbr = async () => {
    setQbrLoading(true);
    try {
      const r = await api.get(`/accounts/${accountId}/group-qbr-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `GroupQBR_${accountId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errDetail(e)); }
    setQbrLoading(false);
  };

  if (!data) return <p className="text-gray-400">{t("loading")}</p>;
  const { totals } = data;
  const att = totals.fc_approved ? Math.round((totals.actual_revenue / totals.fc_approved) * 1000) / 10 : 0;
  const chart = (data.forecast_series || []).map((m) => ({ label: m.month.slice(5), approved: m.approved, pending: m.pending, actual: m.actual }));

  const setCs = async (uid) => {
    try {
      await api.put(`/accounts/${accountId}/global-cs`, { user_id: uid === "none" ? "" : uid });
      toast.success(t("cs_assigned") + " ✓");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const kpis = [
    [t("revenue_ytd"), fmtEur(totals.revenue_ytd), TrendingUp],
    [t("open_pipeline"), fmtEur(totals.open_pipeline), TrendingUp],
    [t("group_complaints"), totals.open_complaints, AlertTriangle],
    [t("attainment"), `${att}%`, TrendingUp],
  ];

  return (
    <div className="space-y-5" data-testid="group-rollup">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={downloadQbr} disabled={qbrLoading} data-testid="group-qbr-btn">
          <FileText className="h-3.5 w-3.5 mr-1.5" />{qbrLoading ? "…" : t("group_qbr")}
        </Button>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(([label, val, Icon], i) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`group-kpi-${i}`}>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{label}</p>
            <p className={`text-xl font-bold font-mono mt-1 ${i === 3 ? (att >= 100 ? "text-emerald-600" : att >= 60 ? "text-[#F5A623]" : "text-red-500") : "text-[#1B2A4A]"}`}>{val}</p>
          </div>
        ))}
      </div>

      {/* Global CS owner */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid="global-cs-panel">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Headset className="h-4 w-4 text-[#1B2A4A]" />{t("global_cs")}
        </div>
        <Badge className="bg-[#1B2A4A]/5 text-[#1B2A4A] hover:bg-[#1B2A4A]/5" data-testid="global-cs-name">
          {data.global_cs_owner_name || t("no_cs_owner")}
        </Badge>
        {isManager && (
          <Select value={data.global_cs_owner_id || "none"} onValueChange={setCs}>
            <SelectTrigger className="w-64 h-8 ml-auto" data-testid="global-cs-select"><SelectValue placeholder={t("assign_global_cs")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("no_cs_owner")}</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Forecast vs actual chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold mb-4">{t("forecast_vs_actual")} · {t("revenue")} ({data.year})</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmtEur(v)} />
            <Legend />
            <Bar dataKey="approved" stackId="fc" name={t("approved_fc")} fill="#94A3B8" />
            <Bar dataKey="pending" stackId="fc" name={t("pending_fc")} fill="#60A5FA" radius={[3, 3, 0, 0]} />
            <Bar dataKey="actual" name={t("total_actual")} fill={AMBER} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Members */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-[#1B2A4A]" />{t("members")} ({data.members.length})</h3>
          <div className="space-y-1.5">
            {data.members.map((m) => (
              <button key={m.id} onClick={() => navigate(`/accounts/${m.id}`)} data-testid="group-member-row"
                className="w-full text-left flex items-center gap-2 py-1.5 border-b border-gray-50 hover:bg-amber-50/40 px-1 rounded">
                <span className="font-medium flex-1 truncate">{m.name}{m.is_root && <span className="text-[#F5A623] ml-1">★</span>}</span>
                <Badge variant="outline" className="text-[10px]">{m.type}</Badge>
                <span className="font-mono text-xs text-gray-500 w-24 text-right">{fmtEur(m.revenue_ytd)}</span>
                {m.open_complaints > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">{m.open_complaints}</Badge>}
              </button>
            ))}
          </div>
        </div>

        {/* Fillers */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Factory className="h-4 w-4 text-[#1B2A4A]" />{t("fillers")} ({data.fillers.length})</h3>
          {data.fillers.length === 0 && <p className="text-sm text-gray-400">–</p>}
          <div className="space-y-1.5">
            {data.fillers.map((f) => (
              <button key={f.id} onClick={() => navigate(`/accounts/${f.id}`)} data-testid="group-filler-row"
                className="w-full text-left flex items-center gap-2 py-1.5 border-b border-gray-50 hover:bg-amber-50/40 px-1 rounded">
                <span className="font-medium flex-1 truncate">{f.name}</span>
                <span className="text-xs text-gray-400">{f.city}</span>
                <Badge variant="outline" className="text-[10px]">{f.type}</Badge>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Consolidated complaints */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-600" />{t("group_complaints")} ({data.open_complaints.length})</h3>
        {data.open_complaints.length === 0 && <p className="text-sm text-gray-400">–</p>}
        <div className="divide-y divide-gray-50">
          {data.open_complaints.map((c) => (
            <button key={c.id} onClick={() => navigate(`/complaints/${c.id}`)} data-testid="group-complaint-row"
              className="w-full text-left flex flex-wrap items-center gap-2 py-2 text-sm hover:bg-amber-50/40 px-1 rounded">
              <span className="font-mono font-medium">{c.complaint_number}</span>
              <span className="text-gray-500 flex-1 truncate">{c.account_name} · {plLabel("defect_categories", c.defect_category)}</span>
              <Badge className={`${SEVERITY_COLORS[c.severity] || ""} hover:bg-inherit`}>{c.severity}</Badge>
              <Badge variant="outline">{c.status}</Badge>
              <span className="font-mono text-xs">{fmtEur(c.claimed_value)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
